import { useEffect, useRef } from 'react';

const HEADER_HEIGHT = 46;

export function useSyncScroll(isMobile) {
  const leftColRef = useRef(null);
  const rightColRef = useRef(null);
  const lastScrollY = useRef(0);
  const leftOffset = useRef(0);

  useEffect(() => {
    const leftCol = leftColRef.current;
    const rightCol = rightColRef.current;

    if (isMobile || !leftCol || !rightCol) return () => {};

    lastScrollY.current = window.scrollY;

    const viewportHeight = () => window.innerHeight - HEADER_HEIGHT;
    const maxOffset = () =>
      Math.max(0, leftCol.scrollHeight - viewportHeight());

    const syncLayout = () => {
      const max = maxOffset();
      rightCol.style.minHeight = max > 0 ? `${viewportHeight() + max}px` : '';
    };

    const handleScroll = () => {
      const delta = window.scrollY - lastScrollY.current;
      lastScrollY.current = window.scrollY;
      const max = maxOffset();

      if (max === 0) {
        leftCol.style.transform = 'translateY(0)';
        return;
      }

      leftOffset.current = Math.min(
        max,
        Math.max(0, leftOffset.current + delta),
      );
      leftCol.style.transform = `translateY(-${leftOffset.current}px)`;
    };

    const observer = new ResizeObserver(() =>
      requestAnimationFrame(syncLayout),
    );
    observer.observe(leftCol);

    syncLayout();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', syncLayout);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', syncLayout);
      rightCol.style.minHeight = '';
    };
  }, [isMobile]);

  const resetScroll = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    leftOffset.current = 0;
    lastScrollY.current = 0;
    if (leftColRef.current) {
      leftColRef.current.style.transform = 'translateY(0)';
    }
  };

  return { leftColRef, rightColRef, resetScroll };
}
